/**
 * Casas decimais preservadas nas notas de correção.
 *
 * O endpoint de correção por IA devolve `double` em `suggestedGrade` e nos scores
 * por critério, e o campo `completness` do payload de correção também é `double`.
 * Arredondar para inteiro apagava a parte fracionária da nota convertida — uma
 * fração de `0,856` viraria `86` em vez de `85,6`.
 */
const GRADE_FRACTION_DIGITS = 2;

/** Menor nota aceita na completude da correção. */
const MIN_GRADE = 0;

/** Maior nota aceita na completude da correção. */
const MAX_GRADE = 100;

/**
 * Passo do slider de completude.
 *
 * A completude é uma nota inteira: o slider anda de 1 em 1, do mesmo jeito que o
 * campo ao lado não aceita casa decimal. As notas que a **IA** devolve continuam
 * fracionárias no painel de resultado — ver {@link GRADE_FRACTION_DIGITS}.
 */
export const GRADE_STEP = 1;

/**
 * Arredonda uma nota para {@link GRADE_FRACTION_DIGITS} casas decimais, sem
 * transformá-la em inteiro.
 *
 * Serve também para descartar o ruído de ponto flutuante acumulado na animação da
 * nota, que interpola entre o valor atual e o sugerido.
 *
 * @param value - Nota bruta, como veio do backend ou do cálculo intermediário
 * @returns Nota arredondada às casas decimais suportadas
 *
 * @example
 * ```ts
 * roundGrade(0.9); // 0.9
 * roundGrade(84.618); // 84.62
 * ```
 */
export function roundGrade(value: number): number {
  const factor = 10 ** GRADE_FRACTION_DIGITS;
  return Math.round(value * factor) / factor;
}

/**
 * Limita a nota ao intervalo 0–100 e arredonda às casas decimais suportadas.
 *
 * @param value - Nota bruta, possivelmente fora do intervalo ou não numérica
 * @returns Nota dentro de 0–100; `0` quando o valor não é finito
 *
 * @example
 * ```ts
 * clampGrade(0.9); // 0.9
 * clampGrade(120); // 100
 * ```
 */
export function clampGrade(value: number): number {
  if (!Number.isFinite(value)) return MIN_GRADE;
  return roundGrade(Math.min(MAX_GRADE, Math.max(MIN_GRADE, value)));
}

/**
 * Limita a completude ao intervalo 0–100 **como inteiro**.
 *
 * A completude é a nota que o corretor digita, e ela não aceita fração. Tudo o que
 * chega nela passa por aqui: o texto digitado, o arrasto do slider, a correção salva
 * antes (o backend guarda `completness` como `double`) e a nota sugerida pela IA.
 *
 * Para as notas da própria IA, que o painel de resultado exibe com casa decimal, use
 * {@link clampGrade}.
 *
 * @param value - Nota bruta, possivelmente fracionária, fora do intervalo ou não numérica
 * @returns Nota inteira dentro de 0–100; `0` quando o valor não é finito
 *
 * @example
 * ```ts
 * clampCompleteness(85.6); // 86
 * clampCompleteness(120); // 100
 * ```
 */
export function clampCompleteness(value: number): number {
  if (!Number.isFinite(value)) return MIN_GRADE;
  return Math.round(Math.min(MAX_GRADE, Math.max(MIN_GRADE, value)));
}

/**
 * Converte uma nota do endpoint de correção por IA para a escala da correção.
 *
 * O endpoint devolve as notas como **fração de 0 a 1** (`suggestedGrade`,
 * `contentScore`, `completenessScore`, `cohesionScore`, `grammarScore`), enquanto a
 * completude da correção — e portanto tudo o que a tela exibe — está na escala 0–100.
 * Sem a conversão, uma nota de `0,9` aparecia como `0,9/100` em vez de `90/100`.
 *
 * @param fraction - Nota fracionária como veio do endpoint de IA
 * @returns Nota na escala 0–100, clampada e arredondada
 *
 * @example
 * ```ts
 * normalizeAiGrade(0.9); // 90
 * normalizeAiGrade(0.856); // 85.6
 * ```
 */
export function normalizeAiGrade(fraction: number): number {
  return clampGrade(fraction * MAX_GRADE);
}

/**
 * Sanitiza o texto digitado no campo de completude, devolvendo o que pode continuar
 * visível no input.
 *
 * O `input[type=number]` não serve de máscara: ele aceita `008`, `000`, `1e3` e
 * expoentes. Aqui o texto é reconstruído a partir do que é aceitável — só dígitos,
 * sem zeros à esquerda e limitado a 100.
 *
 * A completude é inteira, então vírgula e ponto **encerram a leitura**: a parte
 * decimal digitada ou colada é descartada em vez de arredondada, porque arredondar
 * mudaria a nota sem o corretor perceber.
 *
 * @param raw - Texto cru vindo do campo, incluindo o que foi colado
 * @returns Texto sanitizado, pronto para ser reescrito no input
 *
 * @example
 * ```ts
 * sanitizeGradeInput('008'); // '8'
 * sanitizeGradeInput('000'); // '0'
 * sanitizeGradeInput('85,6'); // '85'
 * sanitizeGradeInput('120'); // '100'
 * ```
 */
export function sanitizeGradeInput(raw: string): string {
  const [wholePart = ''] = raw.split(/[,.]/, 1);
  const digits = wholePart.replace(/[^0-9]/g, '').replace(/^0+(?=[0-9])/, '');
  if (digits === '') return '';

  // Passar de 100 não é erro de digitação a ser apagado: o campo trava no máximo
  return Number(digits) > MAX_GRADE ? String(MAX_GRADE) : digits;
}

/**
 * Converte o texto de {@link sanitizeGradeInput} na completude correspondente.
 *
 * Campo vazio vale `0` — o slider e o payload precisam de um número em todo instante.
 *
 * @param text - Texto sanitizado do campo de completude
 * @returns Nota inteira dentro de 0–100
 *
 * @example
 * ```ts
 * parseGradeInput('85'); // 85
 * parseGradeInput(''); // 0
 * ```
 */
export function parseGradeInput(text: string): number {
  return clampCompleteness(Number(text));
}

/**
 * Formata uma nota para exibição, preservando as casas decimais e omitindo-as
 * quando a nota é inteira (`85`, não `85,00`).
 *
 * @param value - Nota a exibir
 * @returns Nota formatada em pt-BR (separador decimal vírgula)
 *
 * @example
 * ```ts
 * formatGrade(0.9); // '0,9'
 * formatGrade(85); // '85'
 * ```
 */
export function formatGrade(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: GRADE_FRACTION_DIGITS,
  }).format(roundGrade(value));
}
